import { useMemo } from 'react';
import { createPortal } from 'react-dom';
import dayjs from 'dayjs';
import { CoachRow } from './tabData';
import CoachPdfReport from './CoachPdfReport';
import { NO_CENTER_AVG } from './coachAnalysis';
import { toMemberViews } from './adapters';
import { useOpsContext } from './useOpsContext';
import { OpsError } from './OpsStates';
import {
    useOpsBriefingAccess,
    useOpsCoachesQuery,
    useOpsMembersQuery,
    useOpsOrdersMonthQuery,
} from '../../services/useOpsBriefingService';

/**
 * 강사 성과 리포트 — **리포트를 여는 유일한 문**.
 *
 * ─────────────────────────────────────────────────────────────
 * 왜 이 파일이 생겼나
 * ─────────────────────────────────────────────────────────────
 * 리포트 진입점이 둘이었는데 서로 **다른 화면**을 그렸습니다.
 *   · 상단 '강사 성과 리포트' 버튼 → 완성본 A4 3장 (CoachPdfReport)
 *   · 강사 목록 행의 '리포트' 버튼 → 우측 슬라이드 패널 (CoachDetailDrawer)
 * 패널은 이전 세대 디자인이었고, 거기서 PDF 를 열면 회원 명부·지시 이행을
 * 아예 안 넘겨서 **같은 강사인데 다른 숫자가 인쇄**됐습니다.
 *
 * 그래서 "리포트에 무엇을 먹이는가"를 여기 한 곳에만 둡니다.
 * 어느 문으로 들어와도 같은 종이가 나옵니다.
 *
 * ─────────────────────────────────────────────────────────────
 * 다 받은 뒤에 그린다
 * ─────────────────────────────────────────────────────────────
 * 예전에는 회원 명부가 오기 전에 빈 배열을 넘겼습니다. 그 찰나에 인쇄를
 * 누르면 '담당 회원 0명'인 리포트가 나옵니다. 인쇄물은 되돌릴 수 없으므로
 * 다 받은 뒤에 폅니다. 못 받은 항목은 **빈 배열이 아니라 undefined** 로
 * 넘겨, 리포트가 '0명'이라고 단언하지 않고 그 칸을 통째로 뺍니다.
 */

type Props = {
    /**
     * 강사 행을 이미 들고 있는 화면(운영 브리핑 안쪽)은 그대로 넘깁니다.
     * 없으면 `coachId` 로 그 달 집계에서 찾아옵니다.
     */
    coach?: CoachRow;
    /**
     * 강사 행이 없는 화면(강사 관리 등)에서 쓰는 입구.
     *
     * 그 화면들의 목록 API 는 `CoachRow` 를 주지 않습니다. 그렇다고 그쪽에서
     * 집계를 따로 불러 조립하게 하면, 리포트에 먹일 데이터를 만드는 규칙이
     * 화면마다 생겨 결국 또 다른 숫자가 인쇄됩니다. **찾는 일도 여기서** 합니다.
     */
    coachId?: string;
    /** 리포트를 뽑을 달 (YYYY-MM). 없으면 이번 달 */
    month?: string;
    onClose: () => void;
};

const CoachReportSheet = ({ coach, coachId, month, onClose }: Props) => {
    const ctx = useOpsContext();
    const monthKey = month ?? dayjs().format('YYYY-MM');

    /**
     * 노출 게이트.
     *
     * 리포트가 쓰는 조회 9개는 전부 서버 게이트 **안**이라, 닫힌 센터에서 부르면
     * 404 가 쏟아집니다. 운영 브리핑 안쪽은 라우트 가드가 이미 막아 주지만
     * 강사 관리·센터 리포트에는 그 가드가 없습니다. 문이 늘었으니 판정도
     * 문마다 두지 말고 여기 한 곳에 둡니다.
     *
     * ⚠️ `allowed` 만 봅니다. 판정 전(isResolving)에도 false 라 조용히 닫힙니다 —
     *    진입점은 잠깐 안 보여도 해가 없고, 잘못 열리면 404 화면이 됩니다.
     */
    const { allowed } = useOpsBriefingAccess(ctx);
    const enabled = ctx.ready && allowed;

    /*
     * 강사 조회는 강사 현황 탭·오늘의 브리핑이 이미 같은 키로 부르고 있습니다.
     * React Query 가 같은 키를 합쳐 주므로 여기서 다시 불러도 요청은 늘지 않고,
     * 대신 어느 문으로 들어와도 **같은 응답**을 보게 됩니다.
     */
    const coachesQuery = useOpsCoachesQuery(ctx, monthKey, enabled);
    /*
     * 회원 명부는 '오늘' 기준입니다.
     * 잔여 회차·만료일·수업 속도는 지금 상태라야 면담에서 쓸 수 있습니다.
     * (지난달 리포트를 열어도 명부는 오늘 것입니다 — 상단 버튼으로 열던
     *  기존 완성본과 같은 기준을 그대로 씁니다.)
     */
    const membersQuery = useOpsMembersQuery(ctx, dayjs().format('YYYY-MM-DD'), enabled);
    /*
     * 🔴 지시는 **그 달 전부(정리된 건 포함)** 를 받는다 (2026-08-31 월 스코프).
     *
     * 기본 목록(`useOpsOrdersQuery`)은 `ARCHIVED` 를 빼고 주는데, 리포트는 그걸
     * **완료로 센다**(`reportComposer.isDone`). 그래서 원장이 정리해 둔 지시가
     * 분자·분모에서 사라져 이행률이 실제보다 낮게 인쇄됐다.
     */
    const ordersQuery = useOpsOrdersMonthQuery(ctx, monthKey, enabled);

    const coaches = useMemo(() => coachesQuery.data?.coaches ?? [], [coachesQuery.data]);
    /*
     * 넘겨받은 행이 있으면 그것을 쓰고, 없으면 id 로 찾는다.
     * 넘겨받은 행을 다시 찾아 덮어쓰지 않는다 — 같은 강사인데 화면마다
     * 다른 스냅숏을 쓰게 되면 인쇄물이 갈린다.
     */
    const target = coach ?? coaches.find((c) => c.coachId === coachId) ?? null;
    /*
     * 비교 기준선 — 목 상수로 메우지 않는다.
     * 서버 평균이 없는데 상수를 끼우면 지어낸 기준으로 매긴 등급이 인쇄된다.
     */
    const centerAvg = coachesQuery.data?.centerAvg ?? NO_CENTER_AVG;

    /*
     * 못 받았으면 빈 배열이 아니라 undefined — '0명'과 '아직 모름'은 다르다.
     *
     * 🔴 **가린 회원은 뺀다** (2026-08-31 교차검증에서 잡힘).
     *    회원 현황은 가린 회원을 목록·인원·금액 합계에서 전부 빼는데
     *    (`OpsBriefingMembersPage` 의 `members`), 이 리포트만 서버 응답을 그대로
     *    썼다. 그래서 원장님이 회원 하나를 가리는 순간
     *    **「지켜야 할 매출」 ≠ Σ 강사별 담당 미사용 선수금** 이 됐다 —
     *    검산 ①(M8 = M2)이 조용히 깨지는 자리다.
     *    서버는 가린 회원도 응답에 실어 보낸다(빼면 되돌릴 길이 없어서).
     *    빼는 것은 화면의 일이고, **모든 화면이 같은 기준으로** 빼야 한다.
     */
    const members = useMemo(
        () =>
            membersQuery.isSuccess
                ? toMemberViews((membersQuery.data.members ?? []).filter((m) => m.hidden !== true))
                : undefined,
        [membersQuery.isSuccess, membersQuery.data]
    );
    const orders = ordersQuery.isSuccess ? (ordersQuery.data ?? []) : undefined;

    const closing = target
        ? (coachesQuery.data?.closing?.find((x) => x.coachId === target.coachId) ?? null)
        : null;

    /*
     * isLoading 이 아니라 isInitialLoading 을 본다.
     * 로그인 정보가 아직 없어 조회를 끈 상태(enabled:false)에서는 isLoading 이
     * 계속 true 라, 그걸 보면 뼈대가 영영 안 걷힌다.
     */
    const loading =
        coachesQuery.isInitialLoading ||
        membersQuery.isInitialLoading ||
        ordersQuery.isInitialLoading;

    /** 리포트를 아직/영영 못 펼 때 쓰는 껍데기 */
    const notice = (body: React.ReactNode) =>
        createPortal(
            <div
                className="fixed inset-0 flex items-center justify-center"
                style={{ background: 'rgba(12,22,40,0.68)', zIndex: 3000, padding: 24 }}
                onClick={onClose}
                role="presentation"
            >
                <div
                    className="rounded-[16px]"
                    style={{ width: 420, maxWidth: '100%', background: '#FFFFFF', padding: 24 }}
                    onClick={(e) => e.stopPropagation()}
                    role="presentation"
                >
                    {body}
                    <button
                        type="button"
                        onClick={onClose}
                        className="rounded-[10px]"
                        style={{
                            marginTop: 16,
                            width: '100%',
                            height: 42,
                            background: '#F4F6FA',
                            border: '1px solid #E3EAF3',
                            color: '#425063',
                            fontSize: 14,
                            fontWeight: 600,
                            cursor: 'pointer',
                        }}
                    >
                        닫기
                    </button>
                </div>
            </div>,
            document.body
        );

    /*
     * 게이트가 닫힌 센터에서는 아무것도 그리지 않는다.
     * 진입점 버튼도 같은 판정으로 숨지만, 여기서 한 번 더 막아
     * 어떤 경로로 열려도 404 화면이 뜨지 않게 한다.
     */
    if (!allowed) return null;

    /*
     * 강사 조회가 실패하면 리포트를 만들 수 없다 —
     * 센터 평균도 동료도 없어 등급이 통째로 성립하지 않는다.
     * 회원 명부·지시는 실패해도 그 칸만 빠지고 나머지는 근거가 된다.
     */
    if (coachesQuery.isError) {
        return notice(
            <OpsError
                title="리포트를 만들 수 없습니다"
                detail="강사 집계를 불러오지 못했습니다"
                onRetry={() => coachesQuery.refetch()}
            />
        );
    }

    if (loading) {
        return notice(
            <div className="flex flex-col items-center" style={{ gap: 12 }}>
                <span style={{ fontSize: 15, fontWeight: 700, color: '#16202E' }}>
                    {target ? `${target.name} 강사 ` : ''}리포트를 만들고 있습니다
                </span>
                <span style={{ fontSize: 13, color: '#7C8794' }} role="status" aria-live="polite">
                    담당 회원과 지시 이행까지 받아온 뒤에 폅니다
                </span>
            </div>
        );
    }

    /*
     * 그 달 집계에 이 강사가 없다.
     *
     * 강사 관리 목록에는 있는데 리포트가 안 나오는 경우가 실제로 있습니다 —
     * 이번 달에 배정된 회원도 수업도 없는 강사입니다. 에러로 처리하면
     * "고장 났나"가 되고, 빈 리포트를 인쇄하면 "실적 0"으로 읽힙니다.
     * 사실만 말하고 닫습니다.
     */
    if (!target) {
        return notice(
            <div className="flex flex-col items-center" style={{ gap: 10 }}>
                <span style={{ fontSize: 26 }}>🗓</span>
                <span style={{ fontSize: 15, fontWeight: 700, color: '#16202E' }}>
                    {dayjs(`${monthKey}-01`).format('YYYY년 M월')} 기록이 없습니다
                </span>
                <span
                    style={{ fontSize: 13, color: '#7C8794', textAlign: 'center', lineHeight: '20px' }}
                >
                    이 강사는 이 달에 배정된 회원도 진행한 수업도 없어
                    <br />
                    리포트를 만들 근거가 없습니다.
                </span>
            </div>
        );
    }

    return (
        <CoachPdfReport
            coach={target}
            month={monthKey}
            centerAvg={centerAvg}
            /* 동료가 비어 있으면 리포트가 '강사 한 명짜리 센터'로 보고 비교를 뺀다 */
            peers={coaches.length > 0 ? coaches : [target]}
            members={members}
            orders={orders}
            closing={closing}
            /*
             * 머리글의 '센터' 칸과 **저장 파일명**이 함께 쓴다.
             * 없으면(구버전 세션) 머리글은 '—', 파일명은 그 자리를 통째로 뺀다.
             */
            centerName={ctx.centerName}
            period={coachesQuery.data?.period ?? null}
            asOf={coachesQuery.data?.asOf ?? null}
            /* 선수금은 달을 안 따라간다 — 서버가 준 실제 기준시각을 그대로 넘긴다 */
            portfolioValueAsOf={coachesQuery.data?.portfolioValueAsOf ?? null}
            onClose={onClose}
        />
    );
};

export default CoachReportSheet;
